import React, { useState } from 'react';

const CRANIAL_NERVE_DATA = [
  {
    nerve: "I - Olfactory",
    function: "Smell",
    findings: ["Intact", "Anosmia", "Hyposmia", "Parosmia", "Not tested"]
  },
  {
    nerve: "II - Optic",
    function: "Vision",
    findings: ["Intact", "Reduced visual acuity", "Visual field defect", "Homonymous hemianopia", "Quadrantanopia", "Central scotoma", "Blindness (unilateral)", "Blindness (bilateral)", "Papilloedema", "Not tested"]
  },
  {
    nerve: "III - Oculomotor",
    function: "Eye movement, pupil, eyelid",
    findings: ["Intact", "Ptosis", "Mydriasis (dilated pupil)", "Loss of pupillary reflex", "Lateral deviation (exotropia)", "Diplopia", "Ophthalmoplegia (partial)", "Ophthalmoplegia (complete)", "Not tested"]
  },
  {
    nerve: "IV - Trochlear",
    function: "Downward & inward eye movement",
    findings: ["Intact", "Diplopia (vertical)", "Head tilt compensation", "Superior oblique palsy", "Not tested"]
  },
  {
    nerve: "V - Trigeminal",
    function: "Facial sensation, mastication",
    findings: ["Intact", "Facial numbness (V1)", "Facial numbness (V2)", "Facial numbness (V3)", "Facial numbness (all divisions)", "Corneal reflex absent", "Jaw deviation on opening", "Masseter weakness", "Trigeminal neuralgia", "Not tested"]
  },
  {
    nerve: "VI - Abducent",
    function: "Lateral eye movement",
    findings: ["Intact", "Medial deviation (esotropia)", "Lateral gaze palsy", "Diplopia (horizontal)", "Not tested"]
  },
  {
    nerve: "VII - Facial",
    function: "Facial expression, taste (ant. 2/3), lacrimation",
    findings: ["Intact", "UMN facial palsy (lower face only)", "LMN facial palsy (whole face)", "Bell's palsy", "Taste loss (anterior 2/3)", "Hyperacusis", "Reduced lacrimation", "Lagophthalmos", "Asymmetrical smile", "Forehead sparing", "Not tested"]
  },
  {
    nerve: "VIII - VestibuloCochlear",
    function: "Hearing & balance",
    findings: ["Intact", "Sensorineural hearing loss", "Conductive hearing loss", "Tinnitus", "Vertigo", "Nystagmus", "Positive Rinne test", "Positive Weber test", "Not tested"]
  },
  {
    nerve: "IX - Glossopharyngeal",
    function: "Taste (post. 1/3), swallowing, gag reflex",
    findings: ["Intact", "Gag reflex absent", "Dysphagia", "Taste loss (posterior 1/3)", "Glossopharyngeal neuralgia", "Not tested"]
  },
  {
    nerve: "X - Vagus",
    function: "Phonation, swallowing, autonomic",
    findings: ["Intact", "Hoarseness / dysphonia", "Dysphagia", "Nasal regurgitation", "Palatal deviation (away from lesion)", "Absent gag reflex", "Autonomic dysfunction", "Not tested"]
  },
  {
    nerve: "XI - Accessory",
    function: "SCM & trapezius movement",
    findings: ["Intact", "Weakness turning head (contralateral)", "Weakness shrugging shoulder", "Trapezius wasting", "SCM wasting", "Not tested"]
  },
  {
    nerve: "XII - Hypoglossal",
    function: "Tongue movement",
    findings: ["Intact", "Tongue deviation (towards lesion - LMN)", "Tongue deviation (away from lesion - UMN)", "Tongue wasting", "Fasciculations on tongue", "Dysarthria", "Dysphagia", "Not tested"]
  }
];

const CranialNerveAssessment = ({ value = {}, onChange }) => {
  // mode: null = choice screen | 'intact' = all intact | 'examine' = full panel
  const [mode, setMode] = useState(() => {
    const hasAnyData = CRANIAL_NERVE_DATA.some(n => (value[n.nerve] || []).length > 0);
    if (!hasAnyData) return null;
    const allMarkedIntact = CRANIAL_NERVE_DATA.every(n => {
      const f = value[n.nerve] || [];
      return f.length === 1 && f[0] === 'Intact';
    });
    return allMarkedIntact ? 'intact' : 'examine';
  });

  const leftNerves  = CRANIAL_NERVE_DATA.slice(0, 6);
  const rightNerves = CRANIAL_NERVE_DATA.slice(6, 12);

  const setFinding = (nerve, finding) => {
    const current = value[nerve] || [];
    const updated = current.includes(finding)
      ? current.filter(f => f !== finding)
      : [...current, finding];
    onChange?.({ ...value, [nerve]: updated });
  };

  const abnormalCount = CRANIAL_NERVE_DATA.filter(n => {
    const findings = value[n.nerve] || [];
    return findings.length > 0 && !findings.every(f => f === 'Intact' || f === 'Not tested');
  }).length;

  const assessedCount = CRANIAL_NERVE_DATA.filter(n => {
    const findings = value[n.nerve] || [];
    return findings.length > 0;
  }).length;

  const NerveCard = ({ nerve, fn, findings }) => {
    const selected = value[nerve] || [];
    const isIntact = selected.includes('Intact');
    const hasAbnormal = selected.length > 0 && !selected.every(f => f === 'Intact' || f === 'Not tested');

    return (
      <div style={{
        borderRadius: '12px', padding: '14px',
        border: `1.5px solid ${hasAbnormal ? '#fca5a5' : isIntact ? '#6ee7b7' : '#e2e8f0'}`,
        background: hasAbnormal ? '#fff8f8' : isIntact ? '#f0fdf4' : '#fff',
        transition: 'all 0.2s', marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{nerve}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{fn}</div>
          </div>
          {hasAbnormal && (
            <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Abnormal
            </span>
          )}
          {isIntact && !hasAbnormal && (
            <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: '#f0fdf4', color: '#059669', border: '1px solid #6ee7b7', fontWeight: 700 }}>
              Intact
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {findings.map(f => {
            const active = selected.includes(f);
            const isIntactBtn = f === 'Intact';
            const isNotTested = f === 'Not tested';
            const color = isIntactBtn ? '#059669'
              : isNotTested ? '#64748b'
              : '#dc2626';
            const activeBg = isIntactBtn ? '#ecfdf5'
              : isNotTested ? '#f1f5f9'
              : '#fef2f2';
            const activeBorder = isIntactBtn ? '#6ee7b7'
              : isNotTested ? '#cbd5e1'
              : '#fca5a5';

            return (
              <button
                key={f}
                type="button"
                onClick={() => setFinding(nerve, f)}
                style={{
                  fontSize: '11px', fontWeight: 600,
                  padding: '4px 10px', borderRadius: '20px', cursor: 'pointer',
                  transition: 'all 0.15s',
                  border: `1px solid ${active ? activeBorder : '#e2e8f0'}`,
                  background: active ? activeBg : 'transparent',
                  color: active ? color : '#64748b'
                }}
              >
                {active ? '✓ ' : ''}{f}
              </button>
            );
          })}
        </div>

        {selected.length > 0 && (
          <div style={{ marginTop: '8px', fontSize: '11px', color: '#94a3b8' }}>
            Selected: {selected.join(', ')}
          </div>
        )}
      </div>
    );
  };

  const markAllIntact = () => {
    const allIntact = {};
    CRANIAL_NERVE_DATA.forEach(n => { allIntact[n.nerve] = ['Intact']; });
    onChange?.(allIntact);
    setMode('intact');
  };

  const handleExamine = () => setMode('examine');

  const handleClear = () => {
    onChange?.({});
    setMode(null);
  };

  /* ── Choice screen (no selection yet) ── */
  if (mode === null) {
    return (
      <div style={{
        padding: '16px 20px', marginBottom: 8,
      }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e40af', marginBottom: 12 }}>
          🧠 Cranial Nerve Assessment
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={markAllIntact} style={{
            flex: 1, padding: '10px 0', borderRadius: 99, fontWeight: 700, fontSize: '0.85rem',
            border: '1.5px solid #10b981', background: '#ecfdf5', color: '#059669',
            cursor: 'pointer', transition: 'all 0.15s',
          }}>
            ✓ All Intact
          </button>
          <button type="button" onClick={handleExamine} style={{
            flex: 1, padding: '10px 0', borderRadius: 99, fontWeight: 700, fontSize: '0.85rem',
            border: '1.5px solid #3b82f6', background: '#fff', color: '#2563eb',
            cursor: 'pointer', transition: 'all 0.15s',
          }}>
            Examine →
          </button>
        </div>
      </div>
    );
  }

  /* ── All Intact summary ── */
  if (mode === 'intact') {
    return (
      <div style={{
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10, marginBottom: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>✅</span>
          <div>
            <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#065f46' }}>All 12 cranial nerves intact</div>
            <div style={{ fontSize: '0.72rem', color: '#6ee7b7', fontWeight: 600 }}>🧠 Cranial Nerve Assessment</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={handleExamine} style={{
            padding: '6px 14px', borderRadius: 99, fontWeight: 700, fontSize: '0.78rem',
            border: '1.5px solid #3b82f6', background: '#eff6ff', color: '#2563eb',
            cursor: 'pointer',
          }}>
            Examine instead
          </button>
          <button type="button" onClick={handleClear} style={{
            padding: '6px 14px', borderRadius: 99, fontWeight: 700, fontSize: '0.78rem',
            border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#94a3b8',
            cursor: 'pointer',
          }}>
            Clear
          </button>
        </div>
      </div>
    );
  }

  /* ── Examine: full panel ── */
  return (
    <div style={{ padding: '20px', position: 'relative', marginBottom: 8 }}>
      <style>{`.cn-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 20px}@media(max-width:480px){.cn-grid{grid-template-columns:1fr!important}}`}</style>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <span style={{ fontWeight: '800', fontSize: '1rem', color: '#1e40af' }}>🧠 Cranial Nerve Assessment</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" onClick={() => setMode(null)}
            style={{ fontSize: '0.75rem', fontWeight: 700, padding: '4px 12px', borderRadius: 99, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#94a3b8', cursor: 'pointer' }}>
            Collapse
          </button>
          <button type="button" onClick={() => setMode(null)}
            style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}
            onMouseEnter={e => e.target.style.color = '#ef4444'}
            onMouseLeave={e => e.target.style.color = '#cbd5e1'}>×</button>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '16px',
        padding: '12px 16px', borderRadius: '12px',
        background: abnormalCount > 0 ? '#fef2f2' : '#f0fdf4',
        border: `1px solid ${abnormalCount > 0 ? '#fca5a5' : '#6ee7b7'}`,
        marginBottom: '16px'
      }}>
        <div style={{ fontSize: '1.6rem', fontWeight: 800, color: abnormalCount > 0 ? '#dc2626' : '#059669' }}>
          {abnormalCount}
        </div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: abnormalCount > 0 ? '#991b1b' : '#065f46' }}>
            {abnormalCount === 0 ? 'All nerves intact / unassessed' : `${abnormalCount} nerve${abnormalCount > 1 ? 's' : ''} with abnormal findings`}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8' }}>Click any finding to mark it. Multiple findings can be selected per nerve.</div>
        </div>
      </div>

      {/* Two-column grid */}
      <div className="cn-grid">
        <div>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cranial Nerves I – VI</div>
          {leftNerves.map(n => (
            <NerveCard key={n.nerve} nerve={n.nerve} fn={n.function} findings={n.findings} />
          ))}
        </div>
        <div>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cranial Nerves VII – XII</div>
          {rightNerves.map(n => (
            <NerveCard key={n.nerve} nerve={n.nerve} fn={n.function} findings={n.findings} />
          ))}
        </div>
      </div>

      {assessedCount > 0 && (
        <button type="button" onClick={handleClear} style={{ marginTop: '10px', fontSize: '12px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>
          Clear all cranial nerve data
        </button>
      )}
    </div>
  );
};

export default CranialNerveAssessment;
